import { useEffect, useState } from 'react';
import { Platform } from 'react-native';

/**
 * Hook to track visual viewport height changes on web
 * Used to detect mobile keyboard opening/closing
 *
 * @returns Current visual viewport height (or window height on native)
 */
export const useVisualViewport = (): number => {
  const [viewportHeight, setViewportHeight] = useState<number>(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return window.visualViewport?.height || window.innerHeight;
    }
    return 0; // Native platforms don't need this
  });

  useEffect(() => {
    // Only run on web
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return;
    }

    const handleResize = () => {
      const height = window.visualViewport?.height || window.innerHeight;
      setViewportHeight(height);
      console.log(`📐 [VisualViewport] Height changed: ${height}px`);
    };

    // Listen to visual viewport resize events
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
      window.visualViewport.addEventListener('scroll', handleResize);
    } else {
      // Fallback for browsers that don't support visualViewport
      window.addEventListener('resize', handleResize);
    }

    // Initial measurement
    handleResize();

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
        window.visualViewport.removeEventListener('scroll', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
    };
  }, []);

  return viewportHeight;
};
